import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import last from 'lodash/last';
import round from 'lodash/round';
import moment from 'moment';
import * as DateHandler from 'utils/DateHandler';
import LocaleUtil from 'utils/LocaleUtil';
import { CONNECT_DOWNLOAD_STATUS_EXPIRED } from 'vd-constants';

/** The locale strings that the expiry sentence needs. */
export interface DownloadExpirationLocale {
  download: {
    expiredAt: string;
    expiredOn: string;
    expiresAt: string;
    expiresInDays: string;
  };
}

/** The locale strings that the downloaded tooltip needs. */
export interface DownloadedTooltipLocale {
  download: {
    downloadedAt: string;
    downloadedOn: string;
    infoDownloadedAt: string;
    infoDownloadedOn: string;
  };
}

/** The part of a download stream that the expiry sentence reads. */
export interface DownloadExpirationStream {
  expiration: number;
  state: string;
}

/** The part of a download stream that the downloaded tooltip reads. */
export interface DownloadedTooltipStream {
  downloadCount: number;
  downloaded: { [key: string]: number[] };
}

/**
 * Format the size of a download for the download table.
 * The Legacy download panel and the Aspen download panel both call this function.
 */
export function getDownloadSize(sizeMB: number) {
  if (sizeMB < 0) {
    // Bug in VMS (version prior to 4.4.6) where download size is reported as negative when larger than 2GB
    return `2GB+`;
  } else if (sizeMB > 1024) {
    return `${round(sizeMB / 1024, 1)}GB`;
  }
  return `${round(sizeMB, 1)}MB`;
}

/**
 * Build the expiry sentence for a download stream.
 * The sentence reports an expiry in the past, then a whole number of days, then a time today.
 * The Legacy download panel and the Aspen download panel both call this function.
 */
export function getDownloadExpiration(
  downloadStream: DownloadExpirationStream,
  locale: DownloadExpirationLocale,
) {
  const { expiration, state } = downloadStream;
  let downloadExpiry = '';

  if (!isNil(expiration)) {
    const { time: expirationTime, date: expirationDate } = DateHandler.toCompactString(expiration);

    const hasExpired =
      expiration <= DateHandler.getTimestamp() || state === CONNECT_DOWNLOAD_STATUS_EXPIRED;

    if (hasExpired) {
      downloadExpiry = isEmpty(expirationDate)
        ? LocaleUtil.getLocalizedStr({
            string: locale.download.expiredAt,
            replacements: { time: expirationTime },
          })
        : LocaleUtil.getLocalizedStr({
            string: locale.download.expiredOn,
            replacements: { date: expirationDate },
          });
    } else {
      const expirationDays = Math.round(
        moment.duration(moment(expiration).diff(moment())).asDays(),
      );
      downloadExpiry =
        expirationDays > 0
          ? LocaleUtil.getLocalizedStr({
              string: locale.download.expiresInDays,
              replacements: { count: expirationDays },
            })
          : LocaleUtil.getLocalizedStr({
              string: locale.download.expiresAt,
              replacements: { time: expirationTime },
            });
    }
  }

  return downloadExpiry;
}

/**
 * Build the tooltip that reports when a download stream was last downloaded.
 * The tooltip stays empty while the download count is zero.
 * The Legacy download panel and the Aspen download panel both call this function.
 */
export function getDownloadedTooltip(
  { downloaded, downloadCount }: DownloadedTooltipStream,
  locale: DownloadedTooltipLocale,
) {
  let lastDownloaded: string | number | Date;
  let downloadedTooltip = '';

  forEach(downloaded, (timestamps) => {
    const lastTimestamp = last(timestamps);
    if (
      isNil(lastDownloaded) ||
      (lastTimestamp && lastTimestamp > new Date(lastDownloaded).valueOf())
    ) {
      if (lastTimestamp) lastDownloaded = lastTimestamp;
      const { time: downloadedTime, date: downloadedDate } =
        DateHandler.toCompactString(lastDownloaded);

      if (downloadCount === 1) {
        downloadedTooltip = isEmpty(downloadedTime)
          ? LocaleUtil.getLocalizedStr({
              string: locale.download.downloadedOn,
              replacements: { date: downloadedDate },
            })
          : LocaleUtil.getLocalizedStr({
              string: locale.download.downloadedAt,
              replacements: { time: downloadedTime },
            });
      } else if (downloadCount > 1) {
        downloadedTooltip = isEmpty(downloadedTime)
          ? LocaleUtil.getLocalizedStr({
              string: locale.download.infoDownloadedOn,
              replacements: { count: downloadCount, date: downloadedDate },
            })
          : LocaleUtil.getLocalizedStr({
              string: locale.download.infoDownloadedAt,
              replacements: { count: downloadCount, time: downloadedTime },
            });
      }
    }
  });

  return downloadedTooltip;
}
